import "../../../src/components/VDialog/VDialog.sass";
import { VThemeProvider } from '../VThemeProvider';
import Activatable from '../../mixins/activatable';
import Dependent from '../../mixins/dependent';
import Detachable from '../../mixins/detachable';
import Overlayable from '../../mixins/overlayable';
import Returnable from '../../mixins/returnable';
import Stackable from '../../mixins/stackable';
import Toggleable from '../../mixins/toggleable';
import ClickOutside from '../../directives/click-outside';
import mixins from '../../util/mixins';
import { removed } from '../../util/console';
import { convertToUnit, keyCodes } from '../../util/helpers';
const baseMixins = mixins(Activatable, Dependent, Detachable, Overlayable, Returnable, Stackable, Toggleable);
export default baseMixins.extend({
    name: 'v-dialog',
    directives: {
        ClickOutside
    },
    props: {
        dark: Boolean,
        disabled: Boolean,
        fullscreen: Boolean,
        light: Boolean,
        maxWidth: [String, Number],
        noClickAnimation: Boolean,
        origin: {
            type: String,
            default: 'center center'
        },
        persistent: Boolean,
        retainFocus: {
            type: Boolean,
            default: true
        },
        scrollable: Boolean,
        transition: {
            type: [String, Boolean],
            default: 'dialog-transition'
        },
        width: [String, Number]
    },
    data() {
        return {
            activatedBy: null,
            animate: false,
            animateTimeout: -1,
            isActive: !!this.value,
            stackMinZIndex: 200,
            previousActiveElement: null
        };
    },
    computed: {
        classes() {
            return {
                [`v-dialog ${this.contentClass}`.trim()]: true,
                'v-dialog--active': this.isActive,
                'v-dialog--persistent': this.persistent,
                'v-dialog--fullscreen': this.fullscreen,
                'v-dialog--scrollable': this.scrollable,
                'v-dialog--animated': this.animate
            };
        },
        contentClasses() {
            return {
                'v-dialog__content': true,
                'v-dialog__content--active': this.isActive
            };
        },
        hasActivator() {
            return Boolean(!!this.$slots.activator || !!this.$scopedSlots.activator);
        }
    },
    watch: {
        isActive(val) {
            if (val) {
                this.show();
                this.hideScroll();
            }
            else {
                var _this$previousActiveE;
                this.removeOverlay();
                this.unbind();
                (_this$previousActiveE = this.previousActiveElement) == null ? void 0 : _this$previousActiveE.focus();
            }
        },
        fullscreen(val) {
            if (!this.isActive)
                return;
            if (val) {
                this.hideScroll();
                this.removeOverlay(false);
            }
            else {
                this.showScroll();
                this.genOverlay();
            }
        }
    },
    created() {
        if (this.$attrs.hasOwnProperty('full-width')) {
            removed('full-width', this);
        }
    },
    beforeMount() {
        this.$nextTick(() => {
            this.isBooted = this.isActive;
            this.isActive && this.show();
        });
    },
    beforeDestroy() {
        if (typeof window !== 'undefined')
            this.unbind();
    },
    methods: {
        animateClick() {
            this.animate = false;
            this.$nextTick(() => {
                this.animate = true;
                window.clearTimeout(this.animateTimeout);
                this.animateTimeout = window.setTimeout(() => this.animate = false, 150);
            });
        },
        closeConditional(e) {
            const target = e.target;
            return !(this._isDestroyed || !this.isActive || this.$refs.content.contains(target) || this.overlay && target && !this.overlay.$el.contains(target)) && this.activeZIndex >= this.getMaxZIndex();
        },
        hideScroll() {
            if (this.fullscreen) {
                document.documentElement.classList.add('overflow-y-hidden');
            }
            else {
                Overlayable.options.methods.hideScroll.call(this);
            }
        },
        show() {
            !this.fullscreen && !this.hideOverlay && this.genOverlay();
            this.$nextTick(() => {
                this.$nextTick(() => {
                    if (!this.$refs.content.contains(document.activeElement)) {
                        this.previousActiveElement = document.activeElement;
                        this.$refs.content.focus();
                    }
                    this.bind();
                });
            });
        },
        bind() {
            window.addEventListener('focusin', this.onFocusin);
        },
        unbind() {
            window.removeEventListener('focusin', this.onFocusin);
        },
        onClickOutside(e) {
            this.$emit('click:outside', e);
            if (this.persistent) {
                this.noClickAnimation || this.animateClick();
            }
            else {
                this.isActive = false;
            }
        },
        onKeydown(e) {
            if (e.keyCode === keyCodes.esc && !this.getOpenDependents().length) {
                if (!this.persistent) {
                    this.isActive = false;
                    const activator = this.getActivator();
                    this.$nextTick(() => activator && activator.focus());
                }
                else if (!this.noClickAnimation) {
                    this.animateClick();
                }
            }
            this.$emit('keydown', e);
        },
        onFocusin(e) {
            if (!e || !this.retainFocus)
                return;
            const target = e.target;
            if (!!target &&
                ![document, this.$refs.content].includes(target) &&
                !this.$refs.content.contains(target) &&
                this.activeZIndex >= this.getMaxZIndex() &&
                !this.getOpenDependentElements().some(el => el.contains(target))) {
                const focusable = this.$refs.content.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
                const el = [...focusable].find(el => !el.hasAttribute('disabled'));
                el && el.focus();
            }
        },
        genContent() {
            return this.showLazyContent(() => [this.$createElement(VThemeProvider, {
                    props: {
                        root: true,
                        light: this.light,
                        dark: this.dark
                    }
                }, [this.$createElement('div', {
                        class: this.contentClasses,
                        attrs: {
                            role: 'document',
                            tabindex: this.isActive ? 0 : undefined,
                            ...this.getScopeIdAttrs()
                        },
                        on: {
                            keydown: this.onKeydown
                        },
                        style: {
                            zIndex: this.activeZIndex
                        },
                        ref: 'content'
                    }, [this.genTransition()])])]);
        },
        genTransition() {
            const content = this.genInnerContent();
            if (!this.transition)
                return content;
            return this.$createElement('transition', {
                props: {
                    name: this.transition,
                    origin: this.origin,
                    appear: true
                }
            }, [content]);
        },
        genInnerContent() {
            const data = {
                class: this.classes,
                ref: 'dialog',
                directives: [{
                        name: 'click-outside',
                        value: {
                            handler: this.onClickOutside,
                            closeConditional: this.closeConditional,
                            include: this.getOpenDependentElements
                        }
                    }, {
                        name: 'show',
                        value: this.isActive
                    }],
                style: {
                    transformOrigin: this.origin
                }
            };
            if (!this.fullscreen) {
                data.style = { ...data.style,
                    maxWidth: convertToUnit(this.maxWidth),
                    width: convertToUnit(this.width)
                };
            }
            return this.$createElement('div', data, this.getContentSlot());
        }
    },
    render(h) {
        return h('div', {
            staticClass: 'v-dialog__container',
            class: {
                'v-dialog__container--attached': this.attach === '' || this.attach === true || this.attach === 'attach'
            },
            attrs: {
                role: 'dialog'
            }
        }, [this.genActivator(), this.genContent()]);
    }
});
